import { useState } from 'react';
import { Flex, Text, Grid, Button, Tooltip, TextArea } from '@radix-ui/themes';
import { ChatBubbleIcon } from '@radix-ui/react-icons';
import { useEdges } from 'reactflow';
import { useCreateSlackMessageMutation } from '../../../../../graphql/queries/CreateSlackMessage.generated';
import { useUpdateSlackMessageMutation } from '../../../../../graphql/queries/UpdateSlackMessage.generated';

import TemplateContentRenderer from '../../../../TemplateContentRenderer';
import { ActionNode, SlackMessageAction } from '../../../../../types';
import SlackInstallationSelect from '../../../../SlackInstallationSelect';
import SlackInstallationChannelSelect from '../../../../SlackInstallationChannelSelect';
import { NodeInput } from '../../../inputs';
import { WorkflowNodeProps } from '../..';
import NodeContainer from '../../Container';
import CategorySection from '../../CategorySection';
import { toast } from '../../../../../utils/toast';

const SlackMessageNode = ({
  id,
  data,
  onChange: handleSave,
}: WorkflowNodeProps<ActionNode>) => {
  const slackAction = data.action as SlackMessageAction;
  const slackMessage = slackAction?.slackMessage;

  const [isEditing, setIsEditing] = useState(!slackMessage?.id);
  const [selectedInstallationId, setSelectedInstallationId] = useState<string | undefined>(
    slackMessage?.installationId,
  );
  const [selectedChannelId, setSelectedChannelId] = useState<
    string | undefined
  >(slackMessage?.channelId);
  const [messageContent, setMessageContent] = useState(slackMessage?.text);
  const edges = useEdges();

  const [createSlackMessage, { loading: isCreating }] =
    useCreateSlackMessageMutation();
  const [updateSlackMessage, { loading: isUpdating }] =
    useUpdateSlackMessageMutation();

  const hasMessageEdge = edges.some(
    (edge) => edge.target === id && edge.targetHandle === 'message',
  );

  const onSave = () => {
    if (!messageContent) return;

    const isCreate = !slackAction?.slackMessage?.id;

    // For create, require installation and channel selection
    if (isCreate && (!selectedInstallationId || !selectedChannelId)) {
      toast.error('Please select both a workspace and channel');
      return;
    }

    toast.info('Saving Slack message...');

    const mutation = isCreate
      ? createSlackMessage({
          variables: {
            input: {
              installation_id: selectedInstallationId!,
              channel_id: selectedChannelId!,
              text: messageContent,
            },
          },
        })
      : updateSlackMessage({
          variables: {
            input: {
              id: slackAction.slackMessage.id,
              text: messageContent,
            },
          },
        });

    mutation
      .then((result) => {
        // Call the parent's onChange to update local state
        if (handleSave) {
          const savedMessage = isCreate
            ? result.data?.createSlackMessage
            : result.data?.updateSlackMessage;

          handleSave(id, {
            ...data,
            action: {
              ...data.action,
              slackMessage: savedMessage,
            },
          });
        }

        toast.success('Slack message saved successfully');
        setIsEditing(false);
      })
      .catch((error) => {
        console.error('Failed to save Slack message:', error);
        toast.error(
          `Failed to ${isCreate ? 'create' : 'update'} Slack message`,
        );
      });
  };

  const handleEditToggle = () => {
    setIsEditing(true);
  };

  return (
    <NodeContainer
      title="Slack Message"
      data={data}
      icon={<ChatBubbleIcon width={16} height={16} />}
      onEdit={handleEditToggle}
      isEditing={isEditing}
    >
      <CategorySection title="Settings">
        <Grid columns="1" gap="3">
          <NodeInput title="Workspace">
            <SlackInstallationSelect
              installationId={selectedInstallationId}
              onValueChange={setSelectedInstallationId}
              disabled={!isEditing}
            />
          </NodeInput>
          <NodeInput title="Channel">
            <SlackInstallationChannelSelect
              installationId={selectedInstallationId}
              channelId={selectedChannelId}
              onValueChange={setSelectedChannelId}
              disabled={!isEditing}
            />
          </NodeInput>
          <NodeInput title="Message">
            {!hasMessageEdge && (
              <>
                {isEditing ? (
                  <TextArea
                    size="1"
                    value={messageContent || ''}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setMessageContent(e.target.value)
                    }
                    placeholder="Enter your Slack message..."
                  />
                ) : (
                  <TemplateContentRenderer
                    content={messageContent || 'No message content'}
                  />
                )}
              </>
            )}
            {hasMessageEdge && (
              <Text size="1" color="gray">
                (Connected via edge)
              </Text>
            )}
          </NodeInput>

          {isEditing && (
            <Flex justify="end" gap="2">
              {!selectedInstallationId ||
              !selectedChannelId ||
              (!hasMessageEdge && !messageContent) ? (
                <Tooltip content="Workspace, channel, and message content (or edge) required">
                  <Button size="1" disabled={true}>
                    Save
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  size="1"
                  onClick={onSave}
                  disabled={isCreating || isUpdating}
                >
                  {isCreating || isUpdating ? 'Saving...' : 'Save'}
                </Button>
              )}
            </Flex>
          )}
        </Grid>
      </CategorySection>
    </NodeContainer>
  );
};

export default SlackMessageNode;
